import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./ForgotPasswordModal.css";
import API_BASE_URL from "../config";

const STEPS = { EMAIL: 1, OTP: 2, RESET: 3 };

const ForgotPasswordModal = ({ show, onClose }) => {
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState("");
  const [otpDigits, setOtpDigits] = useState(Array(6).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const otpRefs = useRef([...Array(6)].map(() => React.createRef()));

  // Inject Bootstrap CSS (kept from your version)
  useEffect(() => {
    const bs = document.createElement("link");
    bs.rel = "stylesheet";
    bs.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bs);

    const icons = document.createElement("link");
    icons.rel = "stylesheet";
    icons.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(icons);

    return () => {
      bs.remove();
      icons.remove();
    };
  }, []);

  // Reset internal state when modal toggles
  useEffect(() => {
    if (!show) return;
    setStep(STEPS.EMAIL);
    setEmail("");
    setOtpDigits(Array(6).fill(""));
    setNewPassword("");
    setConfirmPassword("");
    setResendTimer(0);
    setLoading(false);
  }, [show]);

  // Close on ESC
  useEffect(() => {
    if (!show) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show, onClose]);

  // Resend OTP timer
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  const otp = useMemo(() => otpDigits.join(""), [otpDigits]);

  const passwordStrength = useMemo(() => {
    // simple strength check
    let score = 0;
    if (newPassword.length >= 8) score++;
    if (/[A-Z]/.test(newPassword)) score++;
    if (/[a-z]/.test(newPassword)) score++;
    if (/[0-9]/.test(newPassword)) score++;
    if (/[^A-Za-z0-9]/.test(newPassword)) score++;
    return Math.min(score, 5);
  }, [newPassword]);

  const strengthLabel = ["Very weak", "Weak", "Fair", "Good", "Strong", "Strong"][passwordStrength];
  const strengthPct = (passwordStrength / 5) * 100;

  const handleBackdropClick = (e) => {
    // Close when clicking outside dialog
    if (e.target.classList.contains("fpd-portal")) onClose?.();
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("❌ Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        toast.success("✅ OTP sent to your email");
        setStep(STEPS.OTP);
        setResendTimer(45); // 45s cooldown
        // focus first otp box after a tick
        setTimeout(() => otpRefs.current[0]?.current?.focus(), 50);
      } else {
        const msg = await res.text();
        toast.error( "❌ Email not found");
        //toast.error(msg || "❌ Email not found");

      }
    } catch {
      toast.error("🚨 Server error while sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (res.ok) {
        toast.success("✅ OTP resent");
        setResendTimer(45);
      } else {
        const msg = await res.text();
        toast.error(msg || "❌ Unable to resend OTP");
      }
    } catch {
      toast.error("🚨 Server error");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // only digits
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    if (value && index < 5) otpRefs.current[index + 1]?.current?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.current?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      otpRefs.current[index - 1]?.current?.focus();
    }
    if (e.key === "ArrowRight" && index < 5) {
      e.preventDefault();
      otpRefs.current[index + 1]?.current?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!text) return;
    const next = Array(6).fill("");
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setOtpDigits(next);
    // focus last filled or next
    const last = Math.min(text.length, 6) - 1;
    otpRefs.current[last >= 0 ? last : 0]?.current?.focus();
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      toast.error("❌ Enter the 6-digit OTP");
      return;
    }
    // Optional: if you have a separate verify endpoint, call it here.
    // Otherwise, proceed to reset step (your backend verifies during reset).
    setStep(STEPS.RESET);
    setTimeout(() => document.getElementById("newPassword")?.focus(), 50);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("❌ Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("❌ Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, newPassword }),
      });
      if (res.ok) {
        toast.success("✅ Password reset successful!");
        onClose?.();
      } else {
        const msg = await res.text();
        toast.error(msg || "❌ Invalid OTP");
        // If invalid, take user back to OTP step
        setStep(STEPS.OTP);
      }
    } catch {
      toast.error("🚨 Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fpd-portal modal fade show d-block"
      onMouseDown={handleBackdropClick}
    >
      <div className="fpd-backdrop" />
      <div
        className="modal-dialog modal-dialog-centered fpd-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fpd-title"
        aria-describedby="fpd-desc"
        style={{ maxWidth: 440 }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-content glass-effect shadow-lg border-0 fpd-content animate-pop">
          {/* Header */}
          <div className="modal-header border-0 align-items-start">
            <div className="w-100">
              <div className="d-flex align-items-center justify-content-between">
                <h5 id="fpd-title" className="modal-title fw-semibold mb-0">
                  {step === STEPS.EMAIL ? "Forgot Password" : step === STEPS.OTP ? "Verify OTP" : "Reset Password"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={onClose}
                  disabled={loading}
                />
              </div>

              {/* Step dots */}
              <div className="d-flex gap-2 mt-3">
                {[STEPS.EMAIL, STEPS.OTP, STEPS.RESET].map((s) => (
                  <span
                    key={s}
                    className={`fpd-stepdot ${step >= s ? "active" : ""}`}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <p id="fpd-desc" className="text-white-50 small mt-2 mb-0">
                {step === STEPS.EMAIL &&
                  "Enter your registered email. We’ll send a one-time password to verify it’s you."}
                {step === STEPS.OTP &&
                  "Enter the 6-digit code we sent to your email. You can paste the full code too."}
                {step === STEPS.RESET &&
                  "Create a strong new password and confirm it to finish."}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="modal-body">
            {step === STEPS.EMAIL && (
              <form onSubmit={handleSendOTP} noValidate>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Email address</label>
                  <input
                    type="text"
                    className="form-control fpd-input"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100 fpd-primary"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>
              </form>
            )}

            {step === STEPS.OTP && (
              <form onSubmit={handleVerifyOTP}>
                <div className="mb-3">
                  <label className="form-label fw-semibold">Enter OTP</label>
                  <div className="d-flex justify-content-between gap-2" onPaste={handleOtpPaste}>
                    {otpDigits.map((d, i) => (
                      <input
                        key={i}
                        ref={otpRefs.current[i]}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="form-control text-center fpd-otp"
                        maxLength={1}
                        value={d}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                        disabled={loading}
                        aria-label={`OTP digit ${i + 1}`}
                      />
                    ))}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setStep(STEPS.EMAIL)}
                      disabled={loading}
                    >
                      Back
                    </button>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn btn-link p-0 fpd-resend"
                        onClick={handleResend}
                        disabled={loading || resendTimer > 0}
                        aria-disabled={resendTimer > 0}
                      >
                        {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
                      </button>
                      <button type="submit" className="btn btn-success" disabled={loading || otp.length !== 6}>
                        Verify
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}

            {step === STEPS.RESET && (
              <form onSubmit={handleResetPassword}>
                <div className="mb-3 position-relative">
                  <label className="form-label fw-semibold">New Password</label>
                  <input
                    id="newPassword"
                    type={showNewPwd ? "text" : "password"}
                    className="form-control fpd-input"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="fpd-eye"
                    onClick={() => setShowNewPwd((s) => !s)}
                    aria-label={showNewPwd ? "Hide password" : "Show password"}
                    disabled={loading}
                  >
                    {showNewPwd ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  {/* Strength meter */}
                  <div className="fpd-strength mt-2" aria-hidden="true">
                    <div className="fpd-bar" style={{ width: `${strengthPct}%` }} />
                  </div>
                  <div className="small text-white-50 mt-1">{strengthLabel}</div>
                </div>

                <div className="mb-3 position-relative">
                  <label className="form-label fw-semibold">Confirm Password</label>
                  <input
                    type={showConfirmPwd ? "text" : "password"}
                    className="form-control fpd-input"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="fpd-eye"
                    onClick={() => setShowConfirmPwd((s) => !s)}
                    aria-label={showConfirmPwd ? "Hide password" : "Show password"}
                    disabled={loading}
                  >
                    {showConfirmPwd ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>

                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStep(STEPS.OTP)}
                    disabled={loading}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary fpd-primary"
                    disabled={loading}
                  >
                    {loading ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Inline styles to complement your CSS file */}
      <style>{`
        .fpd-portal { position: fixed; inset: 0; z-index: 1055; }
        .fpd-backdrop { position: absolute; inset: 0; backdrop-filter: blur(6px); background: rgba(3,7,18,.6); }
        .fpd-dialog { z-index: 1056; }
        .fpd-content { background: linear-gradient(180deg, rgba(255,255,255,.16), rgba(255,255,255,.10)); border: 1px solid rgba(255,255,255,.18); border-radius: 20px; color: #fff; }
        .animate-pop { animation: fpdPop .25s ease-out both; }
        @keyframes fpdPop { from { transform: translateY(10px) scale(.98); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
        .fpd-stepdot { width: 10px; height: 10px; border-radius: 999px; background: rgba(255,255,255,.3); }
        .fpd-stepdot.active { background: linear-gradient(90deg, #0072ff, #00c6ff); box-shadow: 0 0 12px rgba(0,114,255,.5); }
        .fpd-input { background: rgba(3,7,18,.25); color: #fff; border: 1px solid rgba(255,255,255,.35); }
        .fpd-input:focus { border-color: #9cc7ff; box-shadow: 0 0 0 4px rgba(0,114,255,.25); }
        .fpd-primary { background: linear-gradient(90deg, #0072ff, #00c6ff); border: none; box-shadow: 0 8px 24px rgba(0,114,255,.35); }
        .fpd-otp { width: 48px; height: 48px; font-weight: 700; font-size: 1.1rem; background: rgba(3,7,18,.25); border: 1px solid rgba(255,255,255,.35); color: #fff; }
        .fpd-otp:focus { border-color: #9cc7ff; box-shadow: 0 0 0 4px rgba(0,114,255,.25); }
        .fpd-resend { color: #b2d3ff; text-decoration: underline; }
        .fpd-eye { position: absolute; right: 10px; top: 36px; background: transparent; border: 0; color: #fff; opacity: .9; }
        .fpd-strength { position: relative; width: 100%; height: 6px; background: rgba(255,255,255,.15); border-radius: 999px; overflow: hidden; }
        .fpd-bar { position: absolute; inset: 0 0 0 0; width: 0; background: linear-gradient(90deg, #ff6b6b, #ffd166, #06d6a0); transition: width .25s ease; }
      `}</style>
    </div>
  );
};

export default ForgotPasswordModal;
